import { useState, type ReactNode } from 'react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { AiComposerProvider } from '@/features/ai/AiComposerContext';
import { AiAssistantOrb } from '@/features/ai/AiAssistantOrb';
import { Sidebar, SidebarContent } from './Sidebar';
import { TopBar } from './TopBar';

/**
 * Authenticated app shell: persistent left sidebar on `lg+`, slide-in
 * drawer on smaller widths, and a bottom-right AI orb that hosts the
 * assistant. Top bar carries notifications, theme, account menu. The
 * main scroll region owns its own overflow so long pages don't shake
 * the shell.
 *
 * The AI orb is the single, always-present entry point to the
 * assistant. It surfaces gentle rotating nudges ("Want to draft an
 * invoice for Acme?") to teach what's possible without committing
 * screen real estate to a fixed dock. Tapping it grows a substantial
 * chat panel anchored to the corner; the page underneath stays fully
 * interactive while the conversation is open.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <AiComposerProvider>
      <div className="flex h-screen w-full overflow-hidden bg-background text-foreground">
        <aside
          className="hidden border-r border-line bg-card lg:flex lg:w-64 lg:shrink-0 lg:flex-col"
          data-testid="sidebar"
        >
          <Sidebar onNavigate={() => setDrawerOpen(false)} />
        </aside>

        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
          <SheetContent
            side="left"
            className="w-72 max-w-[85vw] border-r border-line bg-card p-0 sm:max-w-xs"
            showClose={false}
          >
            <SidebarContent onNavigate={() => setDrawerOpen(false)} />
          </SheetContent>
        </Sheet>

        <div className="flex min-w-0 flex-1 flex-col">
          <TopBar onOpenDrawer={() => setDrawerOpen(true)} />
          <main
            className="scrollbar-thin flex-1 overflow-y-auto"
            data-testid="app-main"
          >
            {children}
          </main>
        </div>

        <AiAssistantOrb />
      </div>
    </AiComposerProvider>
  );
}
